import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MessageCircle, Shield, History, ArrowRight, Flame } from "lucide-react";
import { PRICING, IS_PRELAUNCH } from "@/lib/pricing";
import { Events } from "@/lib/analytics";

const slides = [
  {
    icon: MessageCircle,
    title: "Think before you send",
    text: "SayItBetter analyzes your message and shows how it might land — before you hit send.",
    color: "#4FB3A5",
  },
  {
    icon: Shield,
    title: "Get a risk score, instantly",
    text: "Traffic-light ratings and one-tap rewrites help you communicate clearly without burning bridges.",
    color: "#E9A13B",
  },
  {
    icon: History,
    title: "Your private message coach",
    text: "Save your history, track your progress, and build better habits. Everything stays private to you.",
    color: "#2F5BFF",
  },
  {
    icon: Flame,
    title: "Early Users Get the Best Deal",
    text: "Free users get 3 message checks per day. Unlock all PRO features — unlimited rewrites and more — for just $5.99 for your first year. Limited pre-launch offer.",
    color: "#FEB95A",
    isOffer: true,
  },
];

export default function OnboardingScreen() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  const finish = () => {
    localStorage.setItem("sib_onboarded", "1");
    Events.completeOnboarding();
    navigate("/signup", { replace: true });
  };

  const continueAsGuest = () => {
    localStorage.setItem("sib_onboarded", "1");
    Events.completeOnboarding();
    navigate("/app", { replace: true });
  };

  const next = () => {
    if (step < slides.length - 1) setStep(step + 1);
    else finish();
  };

  const slide = slides[step];
  const Icon = slide.icon;

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F9FB]" data-testid="onboarding-screen">
      {/* Skip */}
      <div className="flex justify-end p-4">
        <button
          onClick={continueAsGuest}
          data-testid="onboarding-skip"
          className="text-sm text-[#6B7280] hover:text-[#1F3A5F] font-medium"
        >
          Skip
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
        <div
          className="w-24 h-24 rounded-3xl flex items-center justify-center mb-8 animate-fadeIn"
          style={{ backgroundColor: `${slide.color}15` }}
          data-testid={`onboarding-icon-${step}`}
        >
          <Icon size={48} style={{ color: slide.color }} strokeWidth={1.8} />
        </div>
        {slide.isOffer && (
          <div className="inline-flex items-center gap-1.5 bg-gradient-to-r from-[#FEE4A4] to-[#FFD27F] text-[#8B4A00] px-3 py-1 rounded-full text-[11px] font-bold mb-3 shadow-sm">
            <Flame size={11} />
            LIMITED PRE-LAUNCH OFFER
          </div>
        )}
        <h2 className="text-3xl font-bold text-[#1F3A5F] mb-4 animate-fadeIn" data-testid="onboarding-title">
          {slide.title}
        </h2>
        <p className="text-[#6B7280] max-w-sm leading-relaxed animate-fadeIn" data-testid="onboarding-text">
          {slide.text}
        </p>
        {slide.isOffer && (
          <p className="text-xs text-[#A0A7B4] mt-4 animate-fadeIn">
            {IS_PRELAUNCH
              ? "No charge today · We'll email you a promo code at launch"
              : `That's ${PRICING.yearly.perMonth} · vs ${PRICING.monthly.label} standard`}
          </p>
        )}
      </div>

      {/* Dots + CTA */}
      <div className="px-8 pb-10 space-y-6">
        <div className="flex items-center justify-center gap-2" data-testid="onboarding-dots">
          {slides.map((_, i) => (
            <div
              key={i}
              className={`h-2 rounded-full transition-all ${
                i === step ? "w-8 bg-[#1F3A5F]" : "w-2 bg-[#E5E7EB]"
              }`}
            />
          ))}
        </div>
        <Button
          onClick={next}
          className="w-full py-6 text-base font-semibold rounded-xl bg-[#1F3A5F] hover:bg-[#2a4a75] text-white"
          data-testid="onboarding-next-btn"
        >
          {slide.isOffer ? PRICING.promo.cta : (step < slides.length - 1 ? "Next" : "Get Started")}
          <ArrowRight className="ml-2" size={18} />
        </Button>
        {step === slides.length - 1 && (
          <>
            <button
              onClick={continueAsGuest}
              data-testid="onboarding-guest-btn"
              className="w-full text-center text-sm text-[#2F5BFF] font-medium hover:underline"
            >
              Try without an account →
            </button>
            <button
              onClick={() => {
                localStorage.setItem("sib_onboarded", "1");
                navigate("/login");
              }}
              data-testid="onboarding-login-btn"
              className="w-full text-center text-sm text-[#6B7280] hover:text-[#1F3A5F]"
            >
              Already have an account? <span className="text-[#2F5BFF] font-medium">Log in</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
